import { Button } from "@/components/Button/Button";
import { Color } from "@/typed/Common";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FC, MouseEventHandler, ReactNode } from "react";

interface BannerProps {
  title?: string;
  description: string;
  onLinkClick?: MouseEventHandler<HTMLButtonElement>;
  linkText?: string;
  icon: ReactNode;
  color?: Color;
}

export const Banner: FC<BannerProps> = ({ title, description, icon, color = "info", linkText, onLinkClick }) => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const xsDown = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <Box
      component="div"
      bgcolor={theme.palette[color].light}
      sx={{
        width: "100%",
        padding: "16px",
        borderRadius: "8px",
        gap: "16px",
        display: "flex",
        justifyContent: smDown ? "flex-start" : "space-between",
        alignItems: smDown ? "flex-start" : "center",
        flexDirection: smDown ? "column" : "row",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          gap: "6px",
          maxWidth: "100%"
        }}
      >
        <Box
          component="div"
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            maxWidth: "100%"
          }}
        >
          {title && (
            <Typography
              variant={"h1"}
              noWrap
              sx={{
                width: "max-content",
                whiteSpace: "normal",
                textWrap: "wrap",
                fontSize: smDown ? "1.2em" : undefined,
                maxWidth: "100%"
              }}
            >
              {title}
            </Typography>
          )}
          {!xsDown && (
            <Box
              component="div"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: theme.palette[color].main
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
        <Typography
          sx={{
            flexGrow: 1,
            maxWidth: "100%"
          }}
        >
          {description}
        </Typography>
      </Box>
      {linkText !== undefined && (
        <Box component="div">
          <Button
            color={color}
            sx={{
              color: color === "info" ? "white" : theme.palette.text.primary
            }}
            onClick={onLinkClick}
          >
            {linkText}
          </Button>
        </Box>
      )}
    </Box>
  );
};
